import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { createVoice } from "@/lib/myVoicesApi";
import { safeApiError } from "@/lib/safeErrorText";

const inp = "w-full rounded-xl border border-white/10 bg-black/40 px-3.5 py-2.5 text-[14px] text-white placeholder:text-white/30 focus:border-cyan-300/50 focus:outline-none";
const lbl = "mb-1 mt-4 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/50";

/** Name the voice and sign the consent record (only your own voice can be trained). */
export default function NewVoiceDialog({ open, onClose, consentText }) {
  const navigate = useNavigate();
  const [name, setName] = useState("Apostle Moore");
  const [notes, setNotes] = useState("Jamaican accent · gospel, reggae and R&B");
  const [signer, setSigner] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const statement = (consentText || "").replace("{signer}", signer.trim() || "________");
  const go = async () => {
    setBusy(true);
    try { const v = await createVoice({ name: name.trim(), notes: notes.trim() || null, signer: signer.trim(), confirm }); navigate(`/create/my-voices/${v.id}`); }
    catch (e) { toast.error("Couldn't create the voice", { description: safeApiError(e) }); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="new-voice-dialog" className="max-w-md border-white/10 bg-[#0b0e1c] text-white">
        <DialogHeader>
          <DialogTitle className="font-display">New private voice</DialogTitle>
          <DialogDescription className="text-white/60">Trained only on recordings you add, kept only in your studio.</DialogDescription>
        </DialogHeader>
        <label className={lbl} htmlFor="nv-name">Voice name</label>
        <input id="nv-name" data-testid="new-voice-name" className={inp} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
        <label className={lbl} htmlFor="nv-notes">Notes (optional)</label>
        <input id="nv-notes" data-testid="new-voice-notes" className={inp} value={notes} maxLength={400} onChange={(e) => setNotes(e.target.value)} />
        <label className={lbl} htmlFor="nv-signer">Your full name (for the consent record)</label>
        <input id="nv-signer" data-testid="new-voice-signer" className={inp} value={signer} maxLength={80} placeholder="e.g. Paul Moore" onChange={(e) => setSigner(e.target.value)} />
        <label className="mt-4 flex items-start gap-3 rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.06] p-3">
          <Checkbox data-testid="new-voice-consent" checked={confirm} onCheckedChange={(v) => setConfirm(Boolean(v))} className="mt-0.5 border-emerald-300/60 data-[state=checked]:bg-emerald-300 data-[state=checked]:text-black" />
          <span className="text-[12px] leading-relaxed text-emerald-50/85"><ShieldCheck className="mr-1 inline h-3.5 w-3.5 text-emerald-300" />{statement}</span>
        </label>
        <button type="button" data-testid="new-voice-create" onClick={go} disabled={busy || !confirm || signer.trim().length < 2 || !name.trim()}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan-300 py-2.5 text-[13px] font-semibold text-black disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Create voice
        </button>
      </DialogContent>
    </Dialog>
  );
}
